import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  SelectorType,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import { AddPaymentInfoTracker } from '@/conversion/tracks/addPaymentInfo';
import type { TrackConversionFn } from '../engine';
import type { IAddPaymentInfoData } from '@/conversion/tracks/addPaymentInfo';
import {
  areAllCardFieldsFilled,
  collectFieldInputs,
  detectCardFields,
  type CardFieldSelectors,
  type DetectedCardFields,
} from './utils/cardFieldDetection';
import {
  CustomerDataStore,
  CustomerMapper,
  detectCustomerFields,
  syncDetectedFieldsToStore,
} from '@/conversion/customer';

const DEDUPE_STORAGE_KEY = 'ag_add_payment_info_tracked';
const FIELD_EVENTS = ['input', 'change', 'blur', 'keyup'] as const;

/**
 * Configuração específica para autoAddPaymentInfo
 */
export interface IAutoAddPaymentInfoConfig {
  isEnabled: boolean;
  /** Valor enviado no payload. `false` desliga a detecção de cartão. */
  paymentMethod?: string | false;
  /** Detecção de campos de cartão. Default: true. */
  detectCardFields?: boolean;
  selectors?: CardFieldSelectors;
  debounceMs?: number;
  throttleMs?: number;
  retryAttempts?: number;
  retryDelay?: number;
  selector?: string;
  selectorType?: SelectorType;
  cartDataSelectors?: {
    items?: string;
    total?: string;
    currency?: string;
  };
  customCartExtractor?: (element: Element) => {
    items?: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>;
    total?: number;
    currency?: string;
  };
}

/**
 * AutoTracker para eventos de adição de informação de pagamento.
 * Dispara por detecção de campos de cartão e/ou por selector (igual ao initiateCheckout).
 */
export class AutoAddPaymentInfoTracker extends BaseAutoTracker {
  private paymentTracker: AddPaymentInfoTracker;
  private autoConfig?: IAutoAddPaymentInfoConfig;
  private trackConversion?: TrackConversionFn;
  private fieldListenerEntries: Array<{
    element: HTMLInputElement;
    eventType: string;
    listener: (event: Event) => void;
  }> = [];
  private detectedFields: DetectedCardFields | null = null;
  private checkTimeoutId?: ReturnType<typeof setTimeout>;
  private lastCheckAt = 0;
  private hasTracked = false;
  private documentSelectorListeners: Array<{
    eventType: string;
    listener: (event: Event) => void;
  }> = [];

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.paymentTracker = new AddPaymentInfoTracker(
      config,
      sessionId,
      visitorId,
      version
    );
  }

  private trackAddPaymentInfo(
    payload: IAddPaymentInfoData
  ): Promise<PixelResponse> {
    if (this.trackConversion) {
      return this.trackConversion('addPaymentInfo', payload);
    }
    return this.paymentTracker.track(payload);
  }

  public async initialize(
    autoConfig: IAutoAddPaymentInfoConfig
  ): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoAddPaymentInfo desabilitado');
      return;
    }

    this.hasTracked = this.readDedupeFlag();
    this.autoConfig = autoConfig;
    this.log(
      `Inicializando AutoAddPaymentInfo (cartão: ${this.shouldDetectCardFields() ? 'on' : 'off'}, selector: ${autoConfig.selector ?? 'nenhum'})`
    );

    this.attachDocumentSelectorListeners();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        void this.setupAll();
      });
    } else {
      await this.setupAll();
    }

    this.isInitialized = true;
  }

  private async setupAll(): Promise<void> {
    if (!this.autoConfig || this.hasTracked) return;

    this.clearFieldListeners();

    if (this.shouldDetectCardFields()) {
      await this.setupCardMonitoring();
    }

    this.observeDOM(() => {
      void this.setupAll();
    });
  }

  private async setupCardMonitoring(): Promise<void> {
    if (!this.autoConfig || this.hasTracked) return;

    this.detectedFields = detectCardFields(this.autoConfig.selectors);
    const inputs = collectFieldInputs(this.detectedFields);

    this.log(`Campos de cartão detectados: ${inputs.length}`);

    inputs.forEach(input => {
      FIELD_EVENTS.forEach(eventType => {
        const listener = () => this.scheduleFieldCheck();
        input.addEventListener(eventType, listener);
        this.fieldListenerEntries.push({ element: input, eventType, listener });
      });
    });

    this.scheduleFieldCheck();
  }

  /**
   * Clique global em capture: pega [data-checkout] dentro ou fora do form,
   * inclusive se o botão for injetado depois ou substituído no DOM.
   */
  private attachDocumentSelectorListeners(): void {
    if (!this.autoConfig || this.documentSelectorListeners.length > 0) return;

    const selectorConfig = this.getSelectorTrackerConfig();
    if (!selectorConfig) return;

    const builtSelector = SelectorUtils.buildSelector(
      selectorConfig.selector,
      selectorConfig.selectorType
    );

    const resolveCheckoutElement = (event: Event): Element | null => {
      const target = event.target;
      if (!(target instanceof Element)) return null;
      try {
        return target.closest(builtSelector);
      } catch {
        return null;
      }
    };

    const onClick = (event: Event) => {
      const element = resolveCheckoutElement(event);
      if (!element) return;
      void this.trackFromSelector(element, 'selector_click');
    };

    const onKeydown = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
      const element = resolveCheckoutElement(event);
      if (!element) return;
      void this.trackFromSelector(element, 'selector_keyboard');
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeydown, true);
    this.documentSelectorListeners.push(
      { eventType: 'click', listener: onClick },
      { eventType: 'keydown', listener: onKeydown }
    );

    this.log(`Listener global de AddPaymentInfo configurado: ${builtSelector}`);
  }

  private clearDocumentSelectorListeners(): void {
    this.documentSelectorListeners.forEach(({ eventType, listener }) => {
      document.removeEventListener(eventType, listener, true);
    });
    this.documentSelectorListeners = [];
  }

  private shouldDetectCardFields(): boolean {
    if (!this.autoConfig) return false;
    if (this.autoConfig.detectCardFields === false) return false;
    if (this.autoConfig.paymentMethod === false) return false;
    return true;
  }

  private resolvePaymentMethod(): string | undefined {
    return typeof this.autoConfig?.paymentMethod === 'string'
      ? this.autoConfig.paymentMethod
      : undefined;
  }

  private getSelectorTrackerConfig(): IAutoTrackerConfig | null {
    if (!this.autoConfig?.selector) return null;

    return {
      isEnabled: this.autoConfig.isEnabled,
      selector: this.autoConfig.selector,
      selectorType: this.autoConfig.selectorType ?? 'attribute',
      debounceMs: 0,
      throttleMs: 0,
      retryAttempts: this.autoConfig.retryAttempts,
      retryDelay: this.autoConfig.retryDelay,
    };
  }

  private syncCustomerFromDom(): void {
    const fields = detectCustomerFields();
    syncDetectedFieldsToStore(fields, (key, value) => {
      CustomerDataStore.getInstance().set(key, value);
    });
  }

  private getReadyCustomer() {
    this.syncCustomerFromDom();
    const store = CustomerDataStore.getInstance();
    if (!store.hasRequiredForAddPaymentInfo()) return null;
    return CustomerMapper.toAddPaymentInfoCustomer(store.get());
  }

  private async trackFromSelector(
    element: Element,
    trigger: string
  ): Promise<PixelResponse> {
    if (!this.autoConfig || this.hasTracked) {
      return {
        success: false,
        error: 'AddPaymentInfo já disparado',
        timestamp: Date.now(),
      };
    }

    const customer = this.getReadyCustomer();
    if (!customer?.email || !customer.phone) {
      this.log(
        'AddPaymentInfo ignorado: customer incompleto (email e phone obrigatórios)',
        CustomerDataStore.getInstance().get()
      );
      return {
        success: false,
        error: 'customer_required',
        timestamp: Date.now(),
      };
    }

    this.hasTracked = true;
    this.markDedupeFlag();
    this.clearFieldListeners();

    const cartData = this.extractCartData(element);
    const paymentMethod = this.resolvePaymentMethod();

    this.log(`Disparando ADD_PAYMENT_INFO via ${trigger}`);

    return this.trackAddPaymentInfo({
      value: cartData.total,
      currency: cartData.currency || 'BRL',
      paymentMethod,
      items: cartData.items,
      customer,
      metadata: {
        source: 'auto_tracker',
        trigger,
      },
    });
  }

  private extractCartData(element: Element): {
    items?: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>;
    total?: number;
    currency?: string;
  } {
    if (!this.autoConfig) {
      return {};
    }

    const data: Record<string, unknown> = {};

    if (this.autoConfig.cartDataSelectors) {
      const selectors = this.autoConfig.cartDataSelectors;

      if (selectors.total) {
        data.total = element.getAttribute(selectors.total);
      }

      if (selectors.currency) {
        data.currency = element.getAttribute(selectors.currency);
      }

      if (selectors.items) {
        data.items = element.getAttribute(selectors.items);
      }
    }

    if (this.autoConfig.customCartExtractor) {
      const customData = this.autoConfig.customCartExtractor(element);
      Object.assign(data, customData);
    }

    return data as {
      items?: Array<{
        item_id: string;
        item_name: string;
        category?: string;
        quantity: number;
        price: number;
      }>;
      total?: number;
      currency?: string;
    };
  }

  private scheduleFieldCheck(): void {
    if (!this.autoConfig || this.hasTracked) return;

    const debounceMs = this.autoConfig.debounceMs ?? 300;

    if (this.checkTimeoutId) {
      clearTimeout(this.checkTimeoutId);
    }

    this.checkTimeoutId = setTimeout(() => {
      void this.checkAndTrack();
    }, debounceMs);
  }

  private async checkAndTrack(): Promise<void> {
    if (!this.autoConfig || this.hasTracked) return;

    const throttleMs = this.autoConfig.throttleMs ?? 1000;
    const now = Date.now();
    if (now - this.lastCheckAt < throttleMs) return;
    this.lastCheckAt = now;

    this.detectedFields = detectCardFields(this.autoConfig.selectors);
    if (!areAllCardFieldsFilled(this.detectedFields)) return;

    const customer = this.getReadyCustomer();
    if (!customer?.email || !customer.phone) {
      this.log(
        'AddPaymentInfo (cartão) ignorado: customer incompleto (email e phone obrigatórios)'
      );
      return;
    }

    this.hasTracked = true;
    this.markDedupeFlag();

    const paymentMethod = this.resolvePaymentMethod() ?? 'CREDIT_CARD';

    this.log('Campos de cartão preenchidos – disparando ADD_PAYMENT_INFO');

    await this.trackAddPaymentInfo({
      paymentMethod,
      customer,
      metadata: {
        source: 'auto_tracker',
        trigger: 'card_fields_completed',
      },
    });

    this.clearFieldListeners();
    this.clearListeners();
  }

  private readDedupeFlag(): boolean {
    try {
      return sessionStorage.getItem(DEDUPE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markDedupeFlag(): void {
    try {
      sessionStorage.setItem(DEDUPE_STORAGE_KEY, '1');
    } catch {
      // ignore storage errors
    }
  }

  private clearFieldListeners(): void {
    this.fieldListenerEntries.forEach(({ element, eventType, listener }) => {
      try {
        element.removeEventListener(eventType, listener);
      } catch {
        // elemento pode ter sido removido do DOM
      }
    });
    this.fieldListenerEntries = [];

    if (this.checkTimeoutId) {
      clearTimeout(this.checkTimeoutId);
      this.checkTimeoutId = undefined;
    }
  }

  public stop(): void {
    this.clearFieldListeners();
    this.clearDocumentSelectorListeners();
    this.clearListeners();
    this.isInitialized = false;
    this.log('AutoAddPaymentInfo parado');
  }

  public async rebuild(): Promise<void> {
    if (this.autoConfig && !this.hasTracked) {
      await this.setupAll();
    }
  }

  public isActive(): boolean {
    return this.isInitialized && !this.hasTracked;
  }

  public getPaymentInfoStats(): {
    hasTracked: boolean;
    detectedFieldCount: number;
    fieldListeners: number;
    selectorListeners: number;
  } {
    const inputs = this.detectedFields
      ? collectFieldInputs(this.detectedFields)
      : [];

    return {
      hasTracked: this.hasTracked,
      detectedFieldCount: inputs.length,
      fieldListeners: this.fieldListenerEntries.length,
      selectorListeners: this.documentSelectorListeners.length,
    };
  }

  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoAddPaymentInfo] ${message}`, data);
    }
  }
}

export const autoAddPaymentInfo = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  _config: IAutoAddPaymentInfoConfig
): void => {
  console.warn(
    'autoAddPaymentInfo: Use a classe AutoAddPaymentInfoTracker diretamente'
  );
};
